import {
    type CSSProperties,
    forwardRef,
    type ImgHTMLAttributes,
    type SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import cn from 'classnames';
import { mergeRefs, type Optional } from '@direct-frontend/stdlib';
import { getAdaptiveImageSources } from './lib/getAdaptiveImageSources';
import { getUniformBackground } from './lib/getUniformBackground';
import { PLACEHOLDER_IMAGE_SRC } from './lib/placeholders';
import { useImageLoadAnimation } from './lib/useImageLoadAnimation';
import { type ImageInfo } from '../../api/image';
import { convertThumbhashToDataURL } from '../../lib/convertThumbhashToDataURL';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import styles from './ResponsiveImage.module.css';

const modifierImage = makeModifierClassname(styles, 'image');
const modifierFallbackImage = makeModifierClassname(styles, 'fallback');

export interface ResponsiveImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'loading'> {
    width?: number | string;
    height?: number | string;
    src?: ImageInfo | string;
    alt: string;
    priority?: boolean;
    fit?: 'cover' | 'contain' | 'auto';
    fallbackSrc?: string;
    borderRadius?: string | 'round';
}

const buildSourceSet = (image: ImageInfo | string | undefined): Optional<string> => {
    if (typeof image === 'string' || typeof image === 'undefined') {
        return image;
    }

    const sources = getAdaptiveImageSources(image);

    /**
     * При клиентском рендеренги в safari версии < 26 img c src-set с одним изображением и большим size (>1000w)
     * ширина изображения определяется как минимальная ширина контейнера вне зависимости от действительной ширины изображения
     * Для исправления используем src-set с сорсом изображения, но без size
     *
     * Проблема также должна исчезнуть при рендере с ssr
     */
    if (sources.length === 1) {
        return sources[0][0];
    }

    return sources.map(([source, width]) => `${source} ${width}w`).join(', ');
};

const buildFallbackSource = (image: ImageInfo | string | undefined): string => {
    if (typeof image === 'string' || typeof image === 'undefined' || !image.file_metadata?.thumb_hash) {
        return PLACEHOLDER_IMAGE_SRC;
    }

    return convertThumbhashToDataURL(image.file_metadata.thumb_hash) ?? PLACEHOLDER_IMAGE_SRC;
};

const buildFitProperty = (fit: ResponsiveImageProps['fit'], image?: ImageInfo | string): 'contain' | 'cover' => {
    if (fit === 'auto') {
        const backgroundColor = typeof image === 'object' ? getUniformBackground(image.file_metadata) : undefined;

        return backgroundColor ? 'contain' : 'cover';
    }

    return fit ?? 'cover';
};

export const ResponsiveImage = forwardRef<HTMLElement, ResponsiveImageProps>(function ResponsiveImage(props, ref) {
    const {
        width = '',
        height = '',
        className,
        src,
        borderRadius,
        sizes = '100cqw',
        priority = false,
        fit = 'auto',
        srcSet = buildSourceSet(src),
        fallbackSrc = buildFallbackSource(src),
        ...other
    } = props;
    const objectFit = buildFitProperty(fit, src);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const [isVisible, setIsVisible] = useState(priority);
    const [containerWidth, setContainerWidth] = useState(0);
    const backgroundColor = typeof src === 'object' ? getUniformBackground(src.file_metadata) : undefined;

    const { isFailed, isLoaded, needsAnimation, onLoad, onError } = useImageLoadAnimation({
        fallbackSrc,
        isVisible,
        ...props,
    });

    const handleLoad = useCallback(
        (event: SyntheticEvent<HTMLImageElement>) => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }

            onLoad?.(event);
        },
        [onLoad]
    );

    const style = useMemo<CSSProperties>(() => {
        const radius = borderRadius === 'round' ? '9999' : borderRadius;

        return {
            ...(radius
                ? {
                      '--ys-current-skin-border-radius': `${radius}px`,
                  }
                : {}),
        } as CSSProperties;
    }, [borderRadius]);

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
    }, [containerRef]);

    // IntersectionObserver для lazy loading с поддержкой горизонтального скролла
    useEffect(() => {
        if (priority || isVisible || !containerRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries.find((entry) => entry.isIntersecting);

                if (entry) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                rootMargin: '250px 0px',
            }
        );

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [isVisible, priority]);

    return (
        <div ref={mergeRefs(ref, containerRef)} style={style} className={cn(className, styles.container)}>
            {!isLoaded && (
                <img
                    width={width}
                    height={height}
                    className={cn(modifierFallbackImage.className, modifierFallbackImage('objectFit', objectFit))}
                    src={fallbackSrc}
                    aria-hidden="true"
                    style={{ backgroundColor }}
                    alt=""
                />
            )}

            <img
                {...other}
                width={width}
                height={height}
                className={cn(
                    modifierImage.className,
                    modifierImage('objectFit', objectFit),
                    modifierImage('loaded', priority || isLoaded),
                    modifierImage('needsAnimation', !priority && needsAnimation)
                )}
                ref={imageRef}
                src={priority ? fallbackSrc : undefined}
                srcSet={isVisible && !isFailed ? srcSet : undefined}
                sizes={containerWidth ? `${containerWidth}px` : sizes}
                loading={priority ? 'eager' : 'lazy'}
                style={{ backgroundColor }}
                onError={onError}
                onLoad={handleLoad}
            />
        </div>
    );
});
